// @ts-nocheck
import React from 'react';
import { Box, IconButton, Slider, Typography } from '@mui/material';
import { FaPlay, FaPause, FaStepForward, FaUndo, FaCog } from 'react-icons/fa';

interface MobileControlPanelProps {
  // Time controls
  formatSliderTime: () => string;
  timeRange: any[];
  loading: boolean;
  sliderValue: number;
  handleSliderChange: (event: Event, newValue: number | number[]) => void;
  
  // Animation controls
  isPlaying: boolean;
  togglePlayAnimation: () => void;
  restartSlider: () => void;
  stepForward: () => void;
  
  // Mobile menu control
  toggleMobileMenu: () => void;
}

/**
 * Mobile control panel component for time and playback controls
 * Displays at the bottom of the map view on mobile devices
 */
const MobileControlPanel: React.FC<MobileControlPanelProps> = ({
  formatSliderTime,
  timeRange,
  loading,
  sliderValue,
  handleSliderChange,
  isPlaying,
  togglePlayAnimation,
  restartSlider,
  stepForward,
  toggleMobileMenu
}) => {
  return (
    <div className="w-full bg-white flex flex-col md:flex-row items-center shadow-md z-10" 
         style={{height: '50px', flexShrink: 0, borderBottom: '1px solid rgba(0,0,0,0.1)', padding: '2px 0'}}>
      {/* Time Controls - Simplified for Mobile */}
      <Box sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 0,
        m: 0
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 0,
          m: 0,
          height: '22px'
        }}>
          {/* Left side: Settings button on mobile */}
          <div className="pl-3">
            <IconButton
              size="small"
              onClick={toggleMobileMenu}
              color="primary"
              sx={{
                color:"#879643",
                mr: 0.5,
                p: 0.6,
                pl: 1,
                width: 30,
                height: 30
              }}
              title="Settings"
            >
              <FaCog style={{ fontSize: '1.2rem' }} />
            </IconButton>
          </div>

          {/* Center: Time display */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              flexGrow: 1,
              textAlign: 'center',
              fontSize: '0.8rem',
              p: 0,
              mb: 0
            }}
          >
            {formatSliderTime()}
          </Typography>

          {/* Right side: Playback controls */}
          <div>
            <IconButton
              size="small"
              onClick={restartSlider}
              disabled={timeRange.length === 0 || loading || sliderValue === 0}
              color="primary"
              sx={{ color:"#879643", m: 0, p: 0.5 }}
              title="Restart"
            >
              <FaUndo style={{ fontSize: '1rem' }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={togglePlayAnimation}
              disabled={timeRange.length === 0 || loading || (sliderValue >= timeRange.length - 1)}
              sx={{
                mx: 0.3,
                p: 0.5,
                width: 26,
                height: 26
              }}
              title={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ?
                <FaPause style={{ color:"#879643", fontSize: '1rem' }} /> :
                <FaPlay style={{ color:"#879643", fontSize: '1rem' }} />}
            </IconButton>
            <IconButton
              size="small"
              onClick={stepForward}
              disabled={timeRange.length === 0 || loading || isPlaying || (sliderValue >= timeRange.length - 1)}
              color="primary"
              sx={{ p: 0.5, m: 0 }}
              title="Step Forward"
            >
              <FaStepForward style={{color:"#879643", fontSize: '1rem' }} />
            </IconButton>
          </div>
        </Box>
        <Slider
          value={sliderValue}
          onChange={handleSliderChange}
          min={0}
          max={timeRange.length > 0 ? timeRange.length - 1 : 0}
          disabled={timeRange.length === 0 || loading}
          valueLabelDisplay="off"
          size="small"
          sx={{
            '& .MuiSlider-thumb': {
              width: 16,
              height: 16,
              backgroundColor: '#879643',
              border: '2px solid white',
              boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
              '&:hover': {
                boxShadow: '0 1px 6px rgba(0,0,0,0.5)'
              }
            },
            '& .MuiSlider-rail': {
              height: 4,
              backgroundColor: '#d0d0d0'
            },
            '& .MuiSlider-track': {
              height: 4,
              backgroundColor: '#879643'
            },
            px: 4, // Increased horizontal padding to prevent cutoff
            mx: 4, // Added larger margin on sides to ensure thumb is fully visible at extremes
            mt: 1,
            mb: 0,
            width: 'calc(100% - 15px)', // Make slider narrower than container to prevent edge clipping
            margin: '0 auto' // Center the slider in its container
          }}
        />
      </Box>
    </div>
  );
};

export default MobileControlPanel;